import {
  botQueryKey,
  useMutationCreateBot,
  useMutationUpdateBot,
  useQueryGetOneBot,
} from "@apis:bot/bot";
import {
  useMutationInsertDeleteUserBots,
  useQueryGetAllUserBotsByBotId,
  userBotQueryKey,
} from "@apis:user_bot/user_bot";
import { SuperDialog } from "@components:SuperDialog";
import { TabPanel } from "@components:TabPanel";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useRBACCheck } from "@hooks:useRBAC";
import { Box, Tab, Tabs } from "@mui/material";
import { AISettings } from "@pages:settings/Tenent/Bots/BotSpecfications/AISettings";
import { General } from "@pages:settings/Tenent/Bots/BotSpecfications/General";
import { FormsInputProps } from "@pages:settings/Tenent/Bots/forms";
import { useToasterStoreActions } from "@stores:global";
import { SyntheticEvent, useState } from "react";
import { useForm } from "react-hook-form";

import { FeatureFlag } from "@:flag";
import { RBACAction, RBACResource } from "@:rbac";

interface BotModalProps {
  isOpen: boolean;
  onClose: () => void;
  botId?: string;
}

export const BotModal = ({ isOpen, onClose, botId }: BotModalProps) => {
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const { data, isLoading } = useQueryGetOneBot(
    { id: botId! },
    {
      enabled: !!botId,
    },
  );

  const canCreateUserBot = useRBACCheck(
    RBACAction.create,
    RBACResource.user_bots,
  );

  const canUpdateUserBot = useRBACCheck(
    RBACAction.create,
    RBACResource.user_bots,
  );

  const { data: userBots, isLoading: userBotsLoading } =
    useQueryGetAllUserBotsByBotId(
      {
        botId: botId!,
      },
      {
        enabled: !!botId,
      },
    );

  const [tab, setTab] = useState(0);

  const handleChangeTab = (_event: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const {
    register,
    handleSubmit,
    formState,
    control,
    watch,
    unregister,
    reset,
    setValue,
    trigger,
  } = useForm<FormsInputProps>({
    defaultValues: {
      name: "",
      ai_settings: {},
      organization_id: "",
      enabled: true,
      user_ids: [],
    },
    values: {
      name: data?.name,
      ai_settings: data?.ai_settings,
      organization_id: data?.organization_id,
      enabled: data?.enabled,
      user_ids: userBots?.map((userBot) => userBot.userId) || [],
    },
    // we need to use mode: 'all' for MUI https://github.com/orgs/react-hook-form/discussions/2357#discussioncomment-42073
    mode: "all",
  });

  const mutationCreateDeleteBotUsers = useMutationInsertDeleteUserBots();

  const mutationCreateBot = useMutationCreateBot({
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: "Failed to create bot",
        variant: "error",
      });
    },
  });

  const mutationUpdateBot = useMutationUpdateBot({
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: "Failed to update bot",
        variant: "error",
      });
    },
  });

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={botId ? "Edit Bot" : "Add Bot"}
      isLoading={isLoading || userBotsLoading}
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "90vh",
          maxHeight: "90vh",
          minWidth: "950px",
          overflowY: "hidden",
        },
      }}
      slotProps={{
        dialogContent: {
          sx: {
            overflowY: "hidden",
          },
        },
      }}
      actionControls={{
        secondary: {
          label: "Cancel",
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
          },
          label: "Save",
          onClick: async () => {
            await handleSubmit(async (data) => {
              if (botId) {
                await mutationUpdateBot.mutateAsync({
                  id: botId,
                  ai_settings: data.ai_settings!,
                  name: data.name!,
                  ...(FeatureFlag.UpdateEnabled
                    ? { enabled: data.enabled! }
                    : {}),
                });

                if (Array.isArray(data.user_ids) && canUpdateUserBot) {
                  await mutationCreateDeleteBotUsers.mutateAsync({
                    botId: botId,
                    userIds: data.user_ids,
                  });
                }

                toastActions.setToaster({
                  open: true,
                  message: "Bot updated",
                  variant: "success",
                });
              } else {
                const createdBot = await mutationCreateBot.mutateAsync({
                  name: data.name!,
                  ai_settings: data.ai_settings!,
                  organization_id: data.organization_id!,
                  ...(FeatureFlag.UpdateEnabled
                    ? { enabled: data.enabled! }
                    : {}),
                });

                if (Array.isArray(data.user_ids) && canCreateUserBot) {
                  await mutationCreateDeleteBotUsers.mutateAsync({
                    botId: createdBot.id,
                    userIds: data.user_ids,
                  });
                }

                toastActions.setToaster({
                  open: true,
                  message: "Bot created",
                  variant: "success",
                });
              }

              await invalidateQueries.run([
                {
                  queryKey: botQueryKey._def,
                },
              ]);

              await invalidateQueries.run([
                {
                  queryKey: userBotQueryKey._def,
                },
              ]);

              onClose();
            })();
          },
          disabled: !formState.isValid,
        },
      }}
    >
      <Box>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tabs value={tab} onChange={handleChangeTab}>
            <Tab label="General" />
            <Tab label="AI Settings" />
          </Tabs>
        </Box>
        <TabPanel index={0} value={tab}>
          <General
            register={register}
            control={control}
            formState={formState}
            watch={watch}
            unregister={unregister}
            type={botId ? "update" : "create"}
            reset={reset}
            setValue={setValue}
            botId={botId}
            trigger={trigger}
          />
        </TabPanel>
        <TabPanel
          index={1}
          value={tab}
          sx={{
            overflowY: "auto",
            maxHeight: "calc(90vh - 200px)",
            height: "100%",
          }}
        >
          <AISettings
            register={register}
            control={control}
            formState={formState}
            watch={watch}
            unregister={unregister}
            type={botId ? "update" : "create"}
            reset={reset}
            setValue={setValue}
            botId={botId}
            trigger={trigger}
          />
        </TabPanel>
      </Box>
    </SuperDialog>
  );
};
